import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';
import { Loader } from '@flintmint/web-shared/components/loader';
import { identify } from '@/analytics';
import { useAppSettingsListener } from '@/hooks/use-app-settings-listener';
import { useSessionStart } from '@/hooks/use-session-start';
import { useUserAccountListener } from '@/hooks/use-user-account-listener';
import { LocalDataProvider } from '@/providers/rxdb/local-data-provider';
import { useServices } from '@/providers/services-provider';
import { AgreementsScreen } from '@/features/agreements';
import {
  AiRefusalInvitation,
  CancelPendingSubscription,
  PlanPicker,
  Recharge,
  StartPlan,
  UserSourceRefusalWatcher,
} from '@/features/billing';

import { appRoutes } from '@/constants/routes';

export const ProtectedRoutes = () => {
  const user = getAuthUser();
  const navigate = useNavigate();
  const { id, emailVerified } = user;
  const { status, authDenial, acceptAgreements } = useServices();

  useSessionStart(Boolean(id && emailVerified));
  useAppSettingsListener();
  useUserAccountListener(id);

  useEffect(() => {
    if (!id) {
      if (navigator.onLine) {
        navigate(appRoutes.login, { replace: false });
      } else {
        navigate(appRoutes.noConnection, { replace: true });
      }
    }
    if (id && !emailVerified) {
      navigate(appRoutes.emailVerification, { replace: false });
    }

    if (id && emailVerified) {
      identify(id);
    }
  }, [emailVerified, id, navigate]);

  useEffect(() => {
    if (!authDenial || authDenial === 'agreements_not_accepted') return;

    navigate(authDenial === 'email_not_verified' ? appRoutes.emailVerification : appRoutes.accessDenied, { replace: true });
  }, [authDenial, navigate]);

  if (!id || !emailVerified) {
    return <Loader fullScreen />;
  }

  // LocalDataProvider renders no UI: opening the local DB in parallel with the access decision
  // keeps boot fast, while the routed tree stays behind the loader until access is confirmed.
  return (
    <LocalDataProvider>
      {authDenial === 'agreements_not_accepted' ? (
        <AgreementsScreen onAccept={acceptAgreements} />
      ) : status === 'ready' ? (
        <>
          <Outlet />
          {/* Over every routed screen, which stays mounted under it. */}
          <PlanPicker />
          <Recharge />
          <StartPlan />
          <CancelPendingSubscription />
          <AiRefusalInvitation />
          <UserSourceRefusalWatcher />
        </>
      ) : (
        <Loader fullScreen />
      )}
    </LocalDataProvider>
  );
};
